import {
  SalaryHistogramResponse,
  SalaryType,
  SurveySubmissionType,
} from '@/lib/generated/graphql-types'
import { SalaryDetails, SalaryHistogramEntry } from '@/modules/salary/types'
import { compensationModelOptions } from '@/modules/surveys/configs/onboarding/compensation-config'
import {
  EMPLOYER_TYPE_OTHER,
  EMPLOYER_TYPE_SELF_EMPLOYED,
  employerTypeDetailsOptions,
  jobTypeOptions,
  LOCUM_TENENS_VALUE,
  practiceSettingOptions,
  PRN_VALUE,
  SALARY_LOCUM_TENENS_VALUE,
  SALARY_PRN_VALUE,
  taxStatusOptions,
} from '@/modules/surveys/configs/onboarding/current-job-pane-config'
import {
  formatAbbreviatedDollar,
  formatDollar,
} from '@/utils/currency-formatters'
import {
  differenceInDays,
  differenceInHours,
  differenceInMonths,
} from 'date-fns'
import { capitalize } from 'lodash'
import { SalaryPayPeriod } from '../table/constants'
import { getSalaryBenefits } from './salary-details-utils'

export const DEFAULT_FILTER_LOCATION = 'United States'

export function formatResponsibilities(responsibilities: string[] | undefined) {
  if (!responsibilities?.length) return ''

  if (
    responsibilities.length === 1 &&
    responsibilities[0].includes('clinical')
  ) {
    return 'Clinical Only'
  }

  if (responsibilities.length <= 2) {
    return responsibilities.map((r) => capitalize(r)).join(' + ')
  }
  return `${capitalize(responsibilities[0])} + (${
    responsibilities.length - 1
  } more)`
}

export function formatCompModel(compModel: string | null | undefined) {
  if (!compModel) return ''
  return (
    compensationModelOptions.find((option) => option.id === compModel)?.label ??
    compModel
  )
}

export function formatJobType(jobType: string | null | undefined): string {
  if (!jobType) return ''
  return jobTypeOptions.find(({ id }) => id === jobType)?.label ?? ''
}

export function getBenefitsCount(salary: SalaryType) {
  return getSalaryBenefits(salary).length
}

export function formatTaxStatus(taxStatus: string[] | null | undefined) {
  if (!taxStatus?.length) return ''
  return taxStatus
    .map((status) => {
      return taxStatusOptions.find(
        // need to replaceAll to normalize the data.
        // some are w-2, and some are w2.
        (option) => option.id === status.replaceAll(/-/gi, ''),
      )?.label
    })
    .join(', ')
}

function isHistogramData(
  data: SalaryDetails | SalaryHistogramResponse,
): data is SalaryHistogramResponse {
  return 'histogram' in data && 'histogramTitle' in data
}
export function calculateTotalNumericCompensation(
  data: SalaryDetails | SalaryHistogramResponse,
): number {
  if (isHistogramData(data)) {
    return data.avgTotalComp
  }
  return data.compTotal?.total ?? 0
}

export function getTotalCompensation(
  salary: SalaryType | SurveySubmissionType,
  options?: { isCompact?: boolean; isHourly?: boolean },
) {
  if (!isSalaryType(salary)) return ''
  const jobType = salary.jobType
  const formatter = options?.isCompact ? formatAbbreviatedDollar : formatDollar
  const total = formatter(
    (salary.baseSalary ?? 0) + (salary.bonus ?? 0) + (salary.otherIncome ?? 0),
  )

  if (
    options?.isHourly ||
    jobType === PRN_VALUE ||
    jobType === LOCUM_TENENS_VALUE
  ) {
    return `${total} / hr`
  }

  return `${total} / yr`
}

export enum SalaryFilterType {
  jobType = 'jobType',
  specialty = 'specialty',
  location = 'location',
}

/**
 * Formats a date to a nice format like "Jan 1, 2021"
 * @param date
 */
export const formatDateToNice = (opts: {
  date: Date
  timezone?: string | null
}) => {
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
  }
  if (opts.timezone) {
    options.timeZone = opts.timezone
  }
  return opts.date.toLocaleDateString('en-US', options)
}

const allEmployerTypeDetailDisplayLabels = Object.values(
  employerTypeDetailsOptions,
).reduce(
  (acc, type) => ({
    ...acc,
    ...(type.reduce(
      (acc, option) => ({ ...acc, [option.id]: option.label }),
      {},
    ) as Record<string, string>),
  }),
  {} as Record<string, string>,
)

export function formatEmployerTypeDetails(
  employerType: string | null | undefined,
  employerDetailsId: string | null | undefined,
  practiceSetting: string | null | undefined,
  isLocumsOrPrn: boolean | undefined,
): string | null {
  if (isLocumsOrPrn) {
    return (
      practiceSettingOptions.find((option) => option.id === practiceSetting)
        ?.label ?? null
    )
  }
  if (employerType === EMPLOYER_TYPE_SELF_EMPLOYED) return 'Self-Employed'
  if (employerType === EMPLOYER_TYPE_OTHER) return 'Other'
  if (!employerDetailsId) return null
  return allEmployerTypeDetailDisplayLabels[employerDetailsId] ?? null
}

export function formatYearsOfExperience(years: string | null | undefined) {
  if (!years) return ''
  return `${years.split(',').join('-')} YOE`
}

function getPercentileRangeIndex(percentileStr: string): number {
  const match = percentileStr.match(/\d+/)
  if (!match) return 0
  const percentile = Number(match[0])
  if (percentile >= 0 && percentile <= 20) return 1
  if (percentile >= 21 && percentile <= 40) return 2
  if (percentile >= 41 && percentile <= 60) return 3
  if (percentile >= 61 && percentile <= 80) return 4
  if (percentile >= 81 && percentile <= 100) return 5
  return 0 // Default case, should not happen if percentile is within 0-100
}

export function findSalaryBucket(
  salary: number,
  histogram: SalaryHistogramEntry[] | undefined,
):
  | {
      label: string | null
      percentileRangeIndex: number
    }
  | undefined {
  if (!histogram?.length) {
    return undefined
  }

  const histogramLength = histogram.length.toString()
  const lastIndex = histogram.length - 1

  if (salary > histogram[lastIndex].salary) {
    return {
      label:
        customPercentileFullLabel.get(histogramLength)?.[lastIndex] ?? null,
      percentileRangeIndex: getPercentileRangeIndex(
        histogram[lastIndex].percentile,
      ),
    }
  }

  for (let i = 0; i < histogram.length; i++) {
    if (salary <= histogram[i].salary) {
      return {
        label: customPercentileFullLabel.get(histogramLength)?.[i] ?? null,
        percentileRangeIndex: getPercentileRangeIndex(histogram[i].percentile),
      }
    }
  }
  return undefined
}

export const customPercentileFullLabel = new Map([
  [
    '9',
    [
      '5th percentile',
      '15th percentile',
      '25th percentile',
      '35th percentile',
      '50th percentile',
      '65th percentile',
      '75th percentile',
      '85th percentile',
      '95th percentile',
    ],
  ],
  [
    '7',
    [
      '10th percentile',
      '25th percentile',
      '25th percentile',
      '50th percentile',
      '75th percentile',
      '90th percentile',
      '95th percentile',
    ],
  ],
  [
    '5',
    [
      '10th percentile',
      '25th percentile',
      '50th percentile',
      '75th percentile',
      '90th percentile',
    ],
  ],
])

export const customPercentileLabel = new Map([
  ['9', ['p5th', null, 'p25th', null, 'p50th', null, 'p75th', null, 'p95th']],
  ['7', ['<p5th', 'p10th', 'p25th', 'p50th', 'p75th', 'p90th', '>p95th']],
  ['5', ['<p10th', 'p25th', 'p50th', 'p75th', '>p90th']],
])

export function isHourly(
  jobType: string | null | undefined,
  payPeriod?: SalaryPayPeriod | null | undefined,
) {
  return (
    jobType === SALARY_PRN_VALUE ||
    jobType === SALARY_LOCUM_TENENS_VALUE ||
    payPeriod === SalaryPayPeriod.Hourly
  )
}

export function getTotalCompAvg(histogramData?: SalaryHistogramResponse) {
  if (!histogramData) return '0'
  return formatDollar(histogramData.avgTotalComp)
}

export function getEstimatedFte(estimatedFte: number | null | undefined) {
  if (!estimatedFte) return ''
  return `${estimatedFte.toFixed(1)} FTE`
}

export function getTimeAgo(originalDate: string | null | undefined): string {
  if (!originalDate) {
    return ''
  }
  const date = new Date(originalDate)
  const now = new Date()
  const hoursDiff = differenceInHours(now, date)
  const daysDiff = differenceInDays(now, date)
  const monthsDiff = differenceInMonths(now, date)

  if (hoursDiff < 24) {
    return `${Math.max(1, hoursDiff)}h` // Ensure at least 1h
  }
  if (daysDiff < 30) {
    return `${daysDiff}d`
  }
  if (daysDiff < 60) {
    return `1mo`
  }
  if (daysDiff < 365) {
    return `${monthsDiff}mo`
  }
  return `1y+`
}

export function getSearchParamValue(
  value: string | string[] | undefined,
): string | undefined {
  if (Array.isArray(value)) {
    return value[0]
  }
  return value
}

export const isSalarySubmission = (
  salary: SalaryType | SurveySubmissionType | undefined,
): salary is SurveySubmissionType => {
  if (!salary) return false
  return 'surveyId' in salary
}

export const isSalaryType = (
  salary: SalaryType | SurveySubmissionType,
): salary is SalaryType => {
  return 'salaryId' in salary
}
